import SparkMD5 from 'spark-md5';
import general_endpoint from './general_endpoint.js';
import req from './requests';
import models from "../model/models";

const requests = req();

class MainAPI {
	constructor() {
		const { get, post, postReturnError, ...rest } = { ...requests };

		this.allowed_drug = new general_endpoint('allowed_drug', requests);

		this.box_check = new general_endpoint('box_check', requests);
		this.box_check.user_stats = () => {
			let midnight = new Date();
			midnight.setHours(0);
			midnight.setMinutes(0);
			midnight.setSeconds(0);
			return get('box_check/user/stats', { after_ts: midnight.getTime() });
		};
		this.box_check.export = (id) => {
			return get('box_check/export', { id });
		};
		this.box_check.paginated = (filter = {}) => {
			return get('box_check/paginated', { filter });
		};
		this.box_check.report = (start, end, id, filter = {}) => {
			return get('box_check/report', { filter });
		};
		this.box_transfer = new general_endpoint('box_transfer', requests);
		this.box_transfer.for_box = (id) => {
			return get('box_transfer/for/box', { id });
		};
		this.box_transfer.for_unit = (id) => {
			return get('box_transfer/for/unit', { id });
		};
		this.box_transfer.report = (after_timestamp, before_timestamp, id, filter = {}) => {
			let data = { filter };
			if (after_timestamp) {
				data.after_timestamp = after_timestamp;
			}
			if (before_timestamp) {
				data.before_timestamp = before_timestamp;
			}
			return get('box_transfer/report', data);
		};
		this.drug = new general_endpoint('drug', requests);
		this.notification_by_hour = new general_endpoint('notification_by_hour', requests);
		this.notification_by_day = new general_endpoint('notification_by_day', requests);
		this.drug.find_by_barcode = (data) => {
			return get('drug/barcode', data);
		};
		this.drug.not_used = (data, end, id, filter = {}) => {
			return get('drug/not/used', { ...data, filter });
		};
		this.drug.used = (data) => {
			return get('drug/only/used', data);
		};
		this.drug.expiring = (data, end, id, filter = ({} = {})) => {
			return get('drug/expiring', { ...data, filter });
		};
		this.drug.expired = (data, end, id, filter = {}) => {
			return get('drug/expired', { ...data, filter });
		};
		this.drug.get_all_with_last_check = (data) => {
			return get('drug/all/last/check', data);
		};
		this.drug.forUnit = (after_timestamp, before_timestamp, unit_id) => {
			let data = { unit_id };
			if (after_timestamp) {
				data.after_timestamp = after_timestamp;
			}
			if (before_timestamp) {
				data.before_timestamp = before_timestamp;
			}
			return get('drug/unit/for', data);
		};
		this.drug.canDelete = (id) => {
			return get('drug/can-delete', { id: id });
		};

		this.drug_box = new general_endpoint('drug_box', requests);
		this.drug_box.for_unit = (id) => {
			return get('drug_box/for/unit', { id });
		};
		this.discrepancy = new general_endpoint('discrepancy', requests);
		this.discrepancy.latest = () => {
			return get('discrepancy/latest', {});
		};
		this.drug_box.find_by_barcode = (data) => {
			return get('drug_box/barcode', data);
		};
		this.drug_box.not_assigned_to_a_unit = (current_drug_box_id) => {
			return get('drug_box/not/assigned/to/unit', { current_drug_box_id });
		};
		this.drug_box.last_used = (data) => {
			return get('drug_box/last', data);
		};

		this.drug.for_box = (drug_box_id) => {
			return get('drug/drug_box/for', { drug_box_id: drug_box_id });
		};
		this.drug_check = new general_endpoint('drug_check', requests);

		this.drug_check.forDrug = (drug_id) => {
			return get(`drug_check/for`, {drug_id});
		};

		this.drug_type = new general_endpoint('drug_type', requests);

		this.disabled_drug_type_category = new general_endpoint('disabled_drug_type_category', requests);
		this.disabled_drug_type_category.all = (user_group_id) => {
      		return get('disabled_drug_type_category/all', {user_group_id});
		}
		this.disabled_drug_type_category.create = (obj) => {
			return postReturnError('disabled_drug_type_category/create', obj);
		}

		this.drug_type.allowed_drugs = () => {
			return get('drug_type/allowed', {});
		};
		this.drug_type.all_with_allowed = () => {
			return get('drug_type/all_with_allowed', {});
		};

		this.drug_transfer = new general_endpoint('drug_transfer', requests);

		this.drug_transfer.for_drug = (id) => {
			return get('drug_transfer/for/drug', { id });
		};

		this.drug_transfer.report = (after_timestamp, before_timestamp, id, filter = {}) => {
			let data = { filter };
			if (after_timestamp) {
				data.after_timestamp = after_timestamp;
			}
			if (before_timestamp) {
				data.before_timestamp = before_timestamp;
			}
			return get('drug_transfer/report', data);
		};

		this.report = new general_endpoint('report', requests);
		this.report.fields = (data) => {
			return get('report/fields', data);
		};
		this.reseal = new general_endpoint('reseal', requests);
		this.stock_move_record = new general_endpoint('stock_move_record', requests);
		this.stock_move_record.administered = (after_timestamp, before_timestamp) => {
			let data = {};
			if (after_timestamp) {
				data.after_timestamp = after_timestamp;
			}
			if (before_timestamp) {
				data.before_timestamp = before_timestamp;
			}
			return get('stock_move_record/administered/official', data);
		};
		this.stock_move_record.administeredOfficial = (after_timestamp, before_timestamp, id, filter = {}) => {
			let data = { filter };
			if (after_timestamp) {
				data.after_timestamp = after_timestamp;
			}
			if (before_timestamp) {
				data.before_timestamp = before_timestamp;
			}
			return get('stock_move_record/administered/official', data);
		};

		this.stock_move_record.administeredGraph = (user_id) => {
			return get('stock_move_record/administered/graph', { user_id });
		};

		this.stock_move_record.forDrug = (drug_id) => {
			return get('stock_move_record/drug/for', { drug_id });
		};

		this.stock_move_record.activityFilter = (filter = {}) => {
			return get('stock_move_record/activity/filter', { filter });
		};
		this.narc_lock_integration = new general_endpoint('narc_lock_integration', requests);

		this.narc_lock_integration.test = ({account, username, password}) => {
			return post('narc_lock_integration/test', {credentials: {account, username, password}});
		}

		this.narc_lock_integration.toggle = ({enable}) => {
			return this.integration.toggle({enable, integration: 'NarcLock'});
		}

		this.narc_lock_integration.lock_lock_audit_report = (_, _end, _id, filter) => {
			return get('narc_lock_integration/lock_lock_audit_report', {filter});
		}

		this.integration = new general_endpoint('user_group_integration', requests);

		this.integration.toggle = ({enable, integration}) => {
			return post('user_group_integration/toggle', {enable, integration});
		}

		this.knox_event = new general_endpoint('knox_event', requests);
		this.knox_event.for_box = async (id) => {
		   return get('knox_event/for/box', {id});
		}
		this.knox_event.for_unit = async (id) => {
		   return get('knox_event/for/unit', {id});
		}

		this.stock_move_record.wasted = (after_timestamp, before_timestamp, id, filter = {}) => {
			let data = { filter };
			if (after_timestamp) {
				data.after_timestamp = after_timestamp;
			}
			if (before_timestamp) {
				data.before_timestamp = before_timestamp;
			}
			return get('stock_move_record/wasted', data);
		};
		this.stock_move_record.forUnit = (after_timestamp, before_timestamp, id) => {
			return get('stock_move_record/unit/for', { after_timestamp, before_timestamp, id });
		};

		this.stock_move_type = new general_endpoint('stock_move_type', requests);
		this.unit = new general_endpoint('unit', requests);
		this.unit.allWithoutBox = () => {
			return get('unit/all/without/box');
		};
		this.user_group = new general_endpoint('user_group', requests);
		this.user_group.create = (data) => {
			data.password = SparkMD5.hash(data.password);
			return post('user_group/create', data);
		};
		this.user_group.allWithMetrics = () => {
			return get('user_group/all/review/metrics');
		};
		this.user_group.current = (data) => {
			return get('user_group/current', data);
		};
		this.user_group.getAsSuperAdmin = (data) => {
			return get('user_group/super_admin/get', data);
		};

		this.user_group.updateMetaData = async (detail) => {
			const res = await post('user_group/update/metadata', detail);
      models.user_group.refreshData();
      return res;
		};

		this.user = new general_endpoint('user', requests);
		this.user.all = (obj) => {
			return get('user/all', { ...obj, returnDisabledUsers: true });
		};
		this.user.create = (data) => {
			data.password = SparkMD5.hash(data.password);
			return post('user/create', data);
		};
		this.user.disable = (id) => {
			return post('user/disable', { id });
		};
		this.user.enable = (id) => {
			return post('user/enable', { id });
		};
		this.user.disable_admin = (id) => {
			return post('user/disable/admin', { id });
		};
		this.user.enable_admin = (id) => {
			return post('user/enable/admin', { id });
		};
		this.user.update = (data) => {
			if (data.password) {
				data.password = SparkMD5.hash(data.password);
			}
			return post('user/update', data);
		};
		this.user.invite = (data) => {
			return postReturnError('user/invite', data);
		};
		this.user.forgot_password = (data) => {
			return post('user/forgot_password', data);
		};
		this.user.profile_picture = (data) => {
			return get('user/profile_picture', data);
		};
		this.user.auth = (data) => {
			return get('user/auth', data);
		};
		this.user.auth_token = (data) => {
			return get('user/auth_token', data);
		};
		this.user.for_group = (group_id) => {
			return get('user/group/for', { id: group_id });
		};
		this.user.login = (data) => {
			//eslint-disable-next-line
			data.password = SparkMD5.hash(data.password);
			return post('user/login', data);
		};
		this.user.loginWithCode = (data) => {
			return post('user/login/with_code', data);
		};

		this.user.verify_user = (data) => {
			//eslint-disable-next-line
			data.password = SparkMD5.hash(data.password);
			return post('user/verify_user', data);
		};

		this.enum = {
			get: (data) => {
				return get('enum/', data);
			},
			all: (data) => {
				return get('enum/all', data);
			}
		};

		this.rootURL = () => {
			let pre = window.location.origin;

			if (pre === 'file://') {
				pre = 'https://admin.logrx.com';
			}
			return pre;
		};
	}
}

let mainAPI = new MainAPI();
window.mainAPI = mainAPI;

export default mainAPI;



// WEBPACK FOOTER //
// ./src/api/MainAPI.js